/**
 * RunView - Series timeline visualization
 * USER-CON-001 UI-002: Shows owned/wanted/missing issues in a horizontal timeline
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Package, Star, ShoppingCart, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, Check, X
} from 'lucide-react';
import { getGaps } from '../../../services/vaultApi';

// Issue cell in the timeline
function IssueCell({ issue, status, onClick, isActive }) {
  const baseClasses = "w-10 h-14 rounded border-2 flex items-center justify-center text-xs font-medium cursor-pointer transition-all relative group";

  const statusClasses = {
    owned: "bg-green-500/20 border-green-500 text-green-400 hover:bg-green-500/30",
    wanted: "bg-yellow-500/20 border-yellow-500 text-yellow-400 hover:bg-yellow-500/30",
    gap: "bg-zinc-800 border-zinc-700 text-zinc-500 hover:border-zinc-600",
    inStock: "bg-orange-500/20 border-orange-500 text-orange-400 hover:bg-orange-500/30",
  };

  return (
    <div
      onClick={() => onClick?.(issue)}
      className={`${baseClasses} ${statusClasses[status]} ${isActive ? 'ring-2 ring-white' : ''}`}
      title={`#${issue.issue_number}${issue.is_key ? ' (KEY)' : ''}`}
    >
      <span className="truncate">{issue.issue_number}</span>

      {/* Badges */}
      {issue.is_key && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full flex items-center justify-center">
          <Star className="w-2 h-2 text-yellow-900" />
        </div>
      )}

      {status === 'inStock' && (
        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-orange-500 rounded-full flex items-center justify-center">
          <ShoppingCart className="w-2 h-2 text-white" />
        </div>
      )}

      {/* Hover tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity">
        #{issue.issue_number}
        {issue.cover_date && ` (${issue.cover_date})`}
        {issue.is_key && <span className="text-yellow-400 ml-1">KEY</span>}
        {status === 'inStock' && <span className="text-orange-400 ml-1">In Stock</span>}
      </div>
    </div>
  );
}

// Series row in the run view
function SeriesRow({ series, onIssueClick, activeIssueId }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Build issue list combining owned and gaps
  const allIssues = useMemo(() => {
    const issues = [];

    // Add owned issues (from user's collection)
    if (series.owned_issues) {
      series.owned_issues.forEach(issue => {
        issues.push({ ...issue, status: 'owned' });
      });
    }

    // Add gaps
    series.gaps.forEach(gap => {
      issues.push({
        issue_id: gap.issue_id,
        issue_number: gap.issue_number,
        cover_date: gap.cover_date,
        is_key: gap.is_key,
        status: gap.in_mdm_inventory ? 'inStock' : 'gap',
        mdm_product_id: gap.mdm_product_id,
        mdm_price: gap.mdm_price,
      });
    });

    // Sort by issue number (handle numeric and string)
    return issues.sort((a, b) => {
      const numA = parseFloat(a.issue_number) || 0;
      const numB = parseFloat(b.issue_number) || 0;
      return numA - numB;
    });
  }, [series]);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [allIssues]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-medium text-white">{series.series_name}</h3>
          <p className="text-xs text-zinc-400">
            {series.publisher && `${series.publisher} `}
            {series.year_began && `(${series.year_began})`}
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500" />
            <span className="text-zinc-400">{series.owned_count} owned</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-zinc-800 border border-zinc-700" />
            <span className="text-zinc-400">{series.gap_count} missing</span>
          </div>
          <div className="text-orange-400 font-medium">
            {series.completion_pct}% complete
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Scroll buttons */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-zinc-800/90 hover:bg-zinc-700 border border-zinc-700 rounded-full flex items-center justify-center"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-zinc-800/90 hover:bg-zinc-700 border border-zinc-700 rounded-full flex items-center justify-center"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        )}

        {/* Issue cells */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-1 overflow-x-auto scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent pb-2"
        >
          {/* c8 ignore start */}
          {allIssues.map((issue, idx) => {
            const active = activeIssueId === issue.issue_id;
            return (<IssueCell
              key={issue.issue_id || idx}
              issue={issue}
              status={issue.status}
              onClick={onIssueClick}
              isActive={active}
            />);
          })}
          {/* c8 ignore stop */}
        </div>
      </div>
    </div>
  );
}

export default function RunView({ items, onIssueClick, activeIssueId }) {
  const [gapsData, setGapsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get unique series from user's items
  const userSeries = useMemo(() => {
    const seriesMap = new Map();
    items.forEach(item => {
      if (item.comic_issue?.series_id) {
        const seriesId = item.comic_issue.series_id;
        if (!seriesMap.has(seriesId)) {
          seriesMap.set(seriesId, {
            id: seriesId,
            name: item.comic_issue.series_name || item.title,
            issues: []
          });
        }
        seriesMap.get(seriesId).issues.push(item);
      }
    });
    return Array.from(seriesMap.values());
  }, [items]);

  // Load gap analysis
  useEffect(() => {
    async function loadGaps() {
      if (userSeries.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const seriesIds = userSeries.map(s => s.id);
        const data = await getGaps({ series_id: seriesIds, include_inventory: true });

        // Merge owned issues into gap data
        const enrichedSeries = data.series.map(series => {
          const userSeriesData = userSeries.find(s => s.id === series.series_id);
          return {
            ...series,
            owned_issues: userSeriesData?.issues.map(item => ({
              issue_id: item.comic_issue_id,
              issue_number: item.issue_number,
              is_key: item.comic_issue?.is_key_issue,
            })) || []
          };
        });

        setGapsData({ ...data, series: enrichedSeries });
      } catch (err) {
        console.error('Failed to load gaps:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadGaps();
  }, [userSeries]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
        <p className="text-zinc-400">Failed to load run data</p>
        <p className="text-sm text-zinc-500 mt-1">{error}</p>
      </div>
    );
  }

  if (!gapsData || gapsData.series.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Package className="w-12 h-12 text-zinc-700 mb-3" />
        <p className="text-zinc-400">No series runs to display</p>
        <p className="text-sm text-zinc-500 mt-1">
          Add comics linked to series to see run completion
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      {/* Summary */}
      <div className="bg-zinc-800/50 rounded-lg p-3 mb-4 flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Analyzing <span className="text-white font-medium">{gapsData.series_analyzed}</span> series
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-zinc-400">
            <span className="text-red-400 font-medium">{gapsData.total_gaps}</span> gaps total
          </span>
          <span className="text-zinc-400">
            <span className="text-orange-400 font-medium">{gapsData.gaps_in_mdm_inventory}</span> in MDM stock
          </span>
        </div>
      </div>

      {/* Series rows */}
      {gapsData.series
        .filter(s => s.gap_count > 0 || s.owned_count > 0)
        .sort((a, b) => b.owned_count - a.owned_count)
        .map(series => (
          <SeriesRow
            key={series.series_id}
            series={series}
            onIssueClick={onIssueClick}
            activeIssueId={activeIssueId}
          />
        ))}
    </div>
  );
}
